import { inject, Injectable } from '@angular/core';
import { DateRange, RelativeDateService } from '@myarvato/shared/calendar';
import {
  ColumnDefinitionBase,
  DataTableSearchDto,
  FilterType,
  MyArvatoTreeTableComponent,
  SelectFilterOption,
  TreeTableFilterService,
} from '@myarvato/shared/table';
import { FilterMetadata } from 'primeng/api';
import { TreeTableLazyLoadEvent } from 'primeng/treetable';
import { ViewApplicationResult, ViewItem, ViewStateData } from '../models';
import { FilterRecord, KpiStateDto } from '@myarvato/shared/models';

interface ColumnProviderService {
  getColumns(): ColumnDefinitionBase[];
}

interface TreeTableFilterComponent {
  columnField(): string;
  filterType(): FilterType;
  textValue: string;
  multiTextValues: string[];
  booleanValue: boolean | null;
  selectedValues: SelectFilterOption[];
  dateRange: DateRange | undefined;
  numericRules: { matchMode: string; numericValue?: number | null }[];
  filterIsActive: { set(value: boolean): void };
}

@Injectable()
export class ViewFilterService {
  private readonly relativeDateService = inject(RelativeDateService);
  private readonly filterService = inject(TreeTableFilterService);

  public applyViewToTable(
    view: ViewItem['value'],
    treeTableComponent: MyArvatoTreeTableComponent,
    initialDataRange: DateRange,
    columnProviderService: ColumnProviderService,
  ): ViewApplicationResult {
    this.clearTableFilters(treeTableComponent);

    const extractedDateRange = this.extractDateRangeFromFilters(view.filters);
    const dateRange = this.relativeDateService.updateDateRangeIfRelative(extractedDateRange as DateRange) ?? initialDataRange;

    let selectedColumns: ColumnDefinitionBase[] | undefined;
    if (view.columns && view.columns.length > 0) {
      selectedColumns = this.restoreColumnsFromView({ value: view } as ViewItem, columnProviderService);
    }

    const activeFilterFields = new Set(Object.keys(view.filters || {}));

    this.applyFiltersWithUISync(treeTableComponent, view.filters || {}, activeFilterFields, {
      first: 0,
      rows: view.rows ?? 10,
    });

    if (view.sortField && treeTableComponent.treetable) {
      treeTableComponent.treetable.sortField = view.sortField;
      treeTableComponent.treetable.sortOrder = view.sortOrder ?? 1;

      // Aktualisiert die Sort-Icon UI nach programmatischer Änderung von sortField/sortOrder.
      const currentFilters = treeTableComponent.treetable.filters;
      treeTableComponent.updateSortUI(false, currentFilters as Record<string, FilterMetadata[]>);
    }

    const treeTable = treeTableComponent.treetable;
    const lazyLoadEvent: TreeTableLazyLoadEvent = {
      first: treeTable.first,
      last: treeTable.first + (treeTable.rows || 10) - 1,
      rows: treeTable.rows || 10,
      filters: treeTable.filters,
      sortField: treeTable.sortField,
      sortOrder: treeTable.sortOrder,
    };

    const filters = this.filterService.buildFiltersFromTreeEvent(lazyLoadEvent, dateRange);

    const searchDto: DataTableSearchDto = {
      first: lazyLoadEvent.first ?? undefined,
      rows: lazyLoadEvent.rows ?? undefined,
      sortField: typeof lazyLoadEvent.sortField === 'string' ? lazyLoadEvent.sortField : undefined,
      sortOrder: typeof lazyLoadEvent.sortOrder === 'number' ? lazyLoadEvent.sortOrder : 1,
      filters: filters,
      globalFilter: undefined,
      multiSortMeta: null,
      anchor: undefined,
    };

    return {
      dateRange: dateRange !== initialDataRange ? dateRange : undefined,
      columns: selectedColumns,
      lazyLoadEvent,
      searchDto,
    };
  }

  public extractCurrentViewState(
    treeLastFilter: TreeTableLazyLoadEvent | undefined,
    currentColumnOrder: string[],
    selectedColumns: ColumnDefinitionBase[],
    kpis?: KpiStateDto,
  ): ViewStateData {
    let normalizedFilters: FilterRecord | undefined;

    if (treeLastFilter?.filters) {
      normalizedFilters = this.filterService.normalizeFiltersForStorage(treeLastFilter.filters);
    }

    const sortField = typeof treeLastFilter?.sortField === 'string' ? treeLastFilter.sortField : undefined;
    const sortOrder = typeof treeLastFilter?.sortOrder === 'number' ? treeLastFilter.sortOrder : 1;
    const rows = typeof treeLastFilter?.rows === 'number' ? treeLastFilter.rows : 10;

    const currentColumns = currentColumnOrder.length > 0 ? currentColumnOrder : selectedColumns.map((col) => col.fieldsIdentifier);

    return {
      normalizedFilters,
      sortField,
      sortOrder,
      rows,
      currentColumns,
      kpis,
    };
  }

  private restoreColumnsFromView(view: ViewItem, columnProviderService: ColumnProviderService): ColumnDefinitionBase[] {
    if (!view.value.columns || view.value.columns.length === 0) {
      return [];
    }

    const allColumns = columnProviderService.getColumns();
    const savedColumns: ColumnDefinitionBase[] = [];

    for (const columnId of view.value.columns) {
      const column = allColumns.find((col: ColumnDefinitionBase) => col.fieldsIdentifier === columnId);
      if (column) {
        savedColumns.push(column);
      }
    }

    return savedColumns;
  }

  private clearTableFilters(treeTableComponent: MyArvatoTreeTableComponent): void {
    const treeTableInstance = treeTableComponent.treetable;
    if (treeTableInstance) {
      treeTableInstance.filters = {};

      if ('filterConstraints' in treeTableInstance) {
        const filterConstraints = (treeTableInstance as { filterConstraints?: FilterRecord }).filterConstraints;
        if (filterConstraints) {
          Object.keys(filterConstraints).forEach((key) => delete filterConstraints[key]);
        }
      }

      if ('_filters' in treeTableInstance) {
        const _filters = (treeTableInstance as { _filters?: FilterRecord })._filters;
        if (_filters) {
          Object.keys(_filters).forEach((key) => delete _filters[key]);
        }
      }

      if (treeTableInstance.columns) {
        treeTableInstance.columns.forEach(
          (col: {
            filterValue?: string | null;
            filterMatchMode?: string | null;
            filter?: object | null;
            filterConstraint?: object | null;
            filterElement?: object | null;
            filterCallback?: object | null;
          }) => {
            if (col.filterValue !== undefined) {
              col.filterValue = null;
            }
            if (col.filterMatchMode !== undefined) {
              col.filterMatchMode = null;
            }
            if (col.filter) {
              col.filter = null;
            }
            if (col.filterConstraint) {
              col.filterConstraint = null;
            }
            if (col.filterElement) {
              col.filterElement = null;
            }
            if (col.filterCallback) {
              col.filterCallback = null;
            }
          },
        );
      }
    }
  }

  private applyFiltersWithUISync(
    treeTableComponent: MyArvatoTreeTableComponent,
    filters: FilterRecord,
    activeFilterFields: Set<string>,
    pagination: { first: number; rows: number },
  ): void {
    treeTableComponent.setExternalFilters(filters, pagination);

    requestAnimationFrame(() => {
      this.clearInactiveFilterComponents(treeTableComponent, activeFilterFields);

      requestAnimationFrame(() => {
        this.synchronizeActiveFilterUI(treeTableComponent, filters);
      });
    });
  }

  private synchronizeActiveFilterUI(treeTableComponent: MyArvatoTreeTableComponent, filters: FilterRecord): void {
    if (!treeTableComponent.columnFilters) {
      return;
    }

    if (treeTableComponent.columnFilters) {
      treeTableComponent.columnFilters.forEach((filterComponent) => {
        const component = filterComponent as unknown as TreeTableFilterComponent;
        const filterField = component.columnField();
        const filterMetadata = filters[filterField];

        if (filterMetadata && filterMetadata.length > 0) {
          this.updateFilterComponentUI(component as unknown as TreeTableFilterComponent, filterMetadata[0]);
          component.filterIsActive.set(true);
        }
      });
    }
  }

  private updateFilterComponentUI(filterComponent: TreeTableFilterComponent, filterMetadata: FilterMetadata): void {
    const filterType = filterComponent.filterType();
    const value = filterMetadata.value;

    switch (filterType) {
      case FilterType.Text:
      case FilterType.Select:
        if (typeof value === 'string') {
          filterComponent.textValue = value;
        }
        break;

      case FilterType.MultiText:
        if (Array.isArray(value)) {
          filterComponent.multiTextValues = value;
        }
        break;

      case FilterType.MultiSelect:
      case FilterType.LazyMultiSelect:
      case FilterType.LazySelect:
        if (Array.isArray(value)) {
          filterComponent.selectedValues = value;
        }
        break;

      case FilterType.Boolean:
        if (typeof value === 'boolean') {
          filterComponent.booleanValue = value;
        }
        break;

      case FilterType.Numeric:
        if (typeof value === 'number') {
          filterComponent.numericRules = [
            {
              matchMode: filterMetadata.matchMode || 'equals',
              numericValue: value,
            },
          ];
        }
        break;
    }
  }

  private clearInactiveFilterComponents(treeTableComponent: MyArvatoTreeTableComponent, activeFilterFields: Set<string>): void {
    setTimeout(() => {
      if (treeTableComponent.columnFilters) {
        treeTableComponent.columnFilters.forEach((filterComponent) => {
          const component = filterComponent as unknown as TreeTableFilterComponent & { clearUIValues(): void };
          const filterField = component.columnField();
          if (!activeFilterFields.has(filterField)) {
            component.clearUIValues();
            component.filterIsActive.set(false);
          }
        });
      }
    }, 0);
  }

  private extractDateRangeFromFilters(filters?: FilterRecord): DateRange | null {
    if (!filters) {
      return null;
    }

    const shippingDateFilter = filters['ShippingDate'];
    if (shippingDateFilter && shippingDateFilter.length > 0) {
      const dateFilter = shippingDateFilter[0];

      if (dateFilter.value && typeof dateFilter.value === 'object' && 'from' in dateFilter.value && 'to' in dateFilter.value) {
        return {
          from: new Date(dateFilter.value.from),
          to: new Date(dateFilter.value.to),
          relativeDateInfo: dateFilter.value.relativeDateInfo,
        };
      } else if (shippingDateFilter.length >= 2) {
        const greaterThanFilter = shippingDateFilter.find((filter) => filter.matchMode === 'gte');
        const lessThanFilter = shippingDateFilter.find((filter) => filter.matchMode === 'lte');

        if (greaterThanFilter?.value && lessThanFilter?.value) {
          return {
            from: new Date(greaterThanFilter.value),
            to: new Date(lessThanFilter.value),
          };
        }
      }
    }

    return null;
  }
}
